import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs'
import {
  distinctUntilChanged,
  map,
  mergeMap,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  withLatestFrom
} from 'rxjs/operators'
import type { Article } from '@flocasts/flosports30-types/dist/entity'
import { pipe } from 'fp-ts/function'
import { ArticleDetailRouteData } from '../articles-routing.module'
import { LOGGER_SERVICE } from '../../logger/logger.config'
import { LoggerService } from '../../logger/logger.interface'
import { ArticleAnalyticsService } from './article-analytics.service'
import { PageMetaService } from '../../libs/page-meta/page-meta.service'
import { AdComponentInputs } from '../../shared/ads/ad/ad-component.interface'
import { AdConfig, AdTargeting, AdTypes, EqAdTargeting } from '../../shared/ads/advertising-service/ad-config'
import {
  AD_ADHESION_SIZE,
  AD_INTERSTITIAL_SIZE,
  AD_LEADERBOARD_LARGE_FLEX_SIZE,
  AD_LEADERBOARD_SMALL_SIZE,
  AD_RECTANGLE_LARGE_SIZE,
  AD_RECTANGLE_SMALL_SIZE,
  AD_SKIN_SIZE
} from '../../shared/ads/ads.utility'
import { environment } from '../../../environments/environment'
import { buildAuthorDate } from '../../shared/author-avatar/author-date/author-date.utility'
import { ArticleView } from './article-view/article-view.component'
import { RelatedContentService } from '../../libs/primary-related-content/related-content.service'
import { ContentItem } from '../../libs/primary-related-content/primary-related-content.component'
import { AuthService } from '../../singleton-services/auth/auth.service'
import { AdComponent } from '../../shared/ads/ad/ad.component'
import { UpdateContentTargetingService } from '../../shared/ads/advertising-service/update-content-targeting.service'
import { buildContentTagString } from '../../shared/analytics/services/segment.utility'
import { ResourceType } from 'src/app/singleton-services/auth/auth.interfaces'

type HasInstance<A> = { instance: A }

@Component({
  selector: 'flo-article',
  templateUrl: './article.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ArticleComponent implements OnDestroy, OnInit {
  private readonly ngOnInit$ = new ReplaySubject<void>(1)
  private readonly ngOnDestroy$ = new Subject<void>()
  private readonly loadedAds$ = new BehaviorSubject<AdComponent[]>([])

  /**
   * This checks to ensure that the article response was obtained by the resolver.
   *
   * If the resolver fails to get the article response from the API, we _must_
   * redirect the user to the 404 page from the resolver, not here. If this fatal log
   * fires, a ticket should be created to handle the error in the resolver.
   */
  private readonly checkResolverWorked: Subscription = this.ngOnInit$
    .pipe(
      switchMap(_ => this.activatedRoute.data),
      take(1)
    )
    .subscribe(routeData => {
      if ((routeData as ArticleDetailRouteData).articleResponse === undefined) {
        this.log.fatal('ArticleComponent: Route Data does not contain the article response from the resolver.', {
          routeData
        })
      } else {
        this.log.trace('ArticleComponent: Received Article from Resolver', {})
      }
    })

  /**
   * Executes the Segment page call when the page loads.
   *
   * @remarks
   * `pipe` from `fp-ts` is used instead of `Observable.prototype.pipe` for
   * better type safety. See the linked Confluence doc for more info.
   *
   * @see {@link https://flocasts.atlassian.net/wiki/spaces/ENG/pages/2179137677/pipe}
   */
  private readonly article$: Observable<Article> = pipe(
    this.activatedRoute.data,
    map((d: ArticleDetailRouteData) => d.articleResponse.data),
    shareReplay(1),
    takeUntil(this.ngOnDestroy$)
  )

  // prettier-ignore
  private readonly pageCall: Subscription = pipe(
    this.article$,
    takeUntil(this.ngOnDestroy$)
  ).subscribe(article => {
    this.analytics.pageArticleDetail({
      id: article.id,
      is_premium: article.premium,
      content_tags: buildContentTagString(article.node?.tags),
      primary_association_id: article?.node?.primary_event_association?.id
    })
  })

  private user$ = this.authService.user$.pipe(shareReplay(1))

  /**
   * Is a user entitled to this content
   */
  private hasAccess$ = this.article$.pipe(
    mergeMap(article => {
      return this.authService.hasAccess(article, ResourceType.Node)
    })
  )

  public readonly view$: Observable<ArticleView> = combineLatest([this.article$, this.user$, this.hasAccess$]).pipe(
    map(([a, user, hasAccess]) => {
      return {
        drillThrough: a.drill_through_link,
        title: a.title,
        tags: a.node?.tags || null,
        assetUrl: a.asset_url,
        content: a.content,
        authorDate: buildAuthorDate(a.author, a.publish_start_date),
        description: a.seo_description,
        heroVideo: a.hero_video_revision,
        showPaywall: !hasAccess,
        userLoggedIn: user.loggedIn,
        slugUri: a.slug_uri,
        previewContent: a.preview_text,
        shareableLink: a.shareable_link
      }
    })
  )

  private readonly setPageMeta: Subscription = combineLatest([this.article$, this.ngOnInit$])
    .pipe(take(1))
    .subscribe(([article]) => {
      this.analytics.setPageMeta(article)
    })

  // prettier-ignore
  constructor(
    private readonly pageMeta: PageMetaService,
    private readonly analytics: ArticleAnalyticsService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly relatedContent: RelatedContentService,
    private authService: AuthService,
    private readonly updateAdTargeting: UpdateContentTargetingService,
    @Inject(LOGGER_SERVICE) private readonly log: LoggerService
  ) {
  }

  public readonly rightRailRelatedContent$: Observable<ReadonlyArray<ContentItem>> = this.article$.pipe(
    // If we don't have a primary association, use the article id
    map(a => (a.drill_through_link === null ? [a.id] : [a.drill_through_link.id, a.id])),
    switchMap(([id, selfId]) => {
      // if OFP id
      if (isNaN(id)) {
        return of([])
      }
      /**
       * Right rail content turned off as part of FloCollege launch {@link https://flocasts.atlassian.net/browse/CXP-8121}
       * Refer to {@link https://flocasts.atlassian.net/browse/CXP-8122} to restore.
       */
      return of([])
    })
  )

  /**
   * Also turned off as part of FloCollege launch {@link https://flocasts.atlassian.net/browse/CXP-8121}
   * Refer to {@link https://flocasts.atlassian.net/browse/CXP-8122} to restore.
   */
  public readonly belowContent$: Observable<ReadonlyArray<ContentItem>> = this.article$.pipe(switchMap(a => of([])))

  public ngOnInit(): void {
    this.ngOnInit$.next()
    this.ngOnInit$.complete()
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()

    this.pageMeta.clearMeta()
  }

  // Ads
  private readonly adConfigMap: Map<AdTypes, AdComponentInputs> = new Map()
  private readonly adTargeting$: Observable<AdTargeting> = combineLatest([
    this.article$,
    this.authService.isPremium$
  ]).pipe(
    map(([a, isPremium]) => ({
      contentIds: a.aggregated_node_ids,
      categorySlugs: a.aggregated_category_slugs,
      analyticsTracking: a,
      isPremium
    })),
    distinctUntilChanged(EqAdTargeting.equals),
    shareReplay(1)
  )

  private refreshAds$ = this.adTargeting$
    .pipe(
      withLatestFrom(this.loadedAds$),
      switchMap(args => {
        const [targeting, loadedAds] = args
        this.log.trace('Article: updating ad targeting for subsequent Article View')
        return this.updateAdTargeting.updateLoadedAdsTargeting(targeting, loadedAds).pipe(map(_ => args))
      }),
      switchMap(([targeting, loadedAds]) => {
        this.log.trace('Article: ad refresh triggered for subsequent Article View', {
          targeting
        })
        return this.updateAdTargeting.refreshLoadedAds(loadedAds)
      }),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe()

  public readonly adLoadedOnChildView = (loadedAd: HasInstance<AdComponent>): void => {
    this.loadedAds$.next([...this.loadedAds$.getValue(), loadedAd.instance])
  }

  public readonly adConfigs$: Observable<Map<AdTypes, AdComponentInputs>> = this.adTargeting$.pipe(
    map(targeting => {
      this.adConfigMap.set('skin', {
        config: {
          adConfig: AdConfig('news/post/atf', AD_SKIN_SIZE, 'news-ad-skin', targeting),
          additionalOpts: {
            deferClick: environment.advertising.preventClickTimeout
          }
        }
      })

      this.adConfigMap.set('leaderboard-large', {
        config: {
          adConfig: AdConfig(
            'news/post/728x90_flex',
            AD_LEADERBOARD_LARGE_FLEX_SIZE,
            'news-leaderboard-large',
            targeting
          ),
          additionalOpts: { deferClick: environment.advertising.preventClickTimeout }
        }
      })

      this.adConfigMap.set('leaderboard-small', {
        config: {
          adConfig: AdConfig('news/post/728x90_flex', AD_LEADERBOARD_SMALL_SIZE, 'news-leaderboard-small', targeting),
          additionalOpts: { deferClick: environment.advertising.preventClickTimeout }
        }
      })

      this.adConfigMap.set('adhesion', {
        config: {
          adConfig: AdConfig('news/post/adhesion', AD_ADHESION_SIZE, 'news-adhesion', targeting),
          additionalOpts: { maxRefreshTime: 30000 }
        }
      })

      this.adConfigMap.set('rectangle-small', {
        config: {
          adConfig: AdConfig('news/post/300x250_1', AD_RECTANGLE_SMALL_SIZE, 'news-ad-first-rectangle', targeting)
        }
      })

      this.adConfigMap.set('rectangle-large-sticky', {
        config: {
          adConfig: AdConfig('news/post/300x250_2', AD_RECTANGLE_LARGE_SIZE, 'news-ad-second-rectangle', targeting),
          additionalOpts: { maxRefreshTime: 60000 }
        }
      })

      this.adConfigMap.set('interstitial', {
        config: {
          adConfig: AdConfig('news/post/interstitial', AD_INTERSTITIAL_SIZE, 'news-interstitial', targeting)
        }
      })

      // Ads team requests this never refreshes
      this.adConfigMap.set('rectangle-mobile', {
        config: {
          adConfig: AdConfig('news/post/300x250_1', AD_RECTANGLE_SMALL_SIZE, 'news-mobile-rectangle', targeting)
        }
      })

      return this.adConfigMap
    })
  )
}
